import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/dashboard", { replace: true }); }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome to Coldforge");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--cf-bg)" }}>
      {/* Left - hero */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden"
        style={{ background: "var(--cf-surface)", borderRight: "1px solid var(--cf-border)" }}
      >
        {/* Subtle texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1690983320828-c01b88baacb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwaW5kdXN0cmlhbCUyMHN0ZWVsJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODEyNzMwNjh8MA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(1)"
          }}
        />

        <div className="relative">
          <div className="font-display text-3xl font-bold tracking-tight uppercase cf-text-base">
            COLDFORGE<span style={{ color: "var(--cf-accent)" }}>.</span>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest mt-2 cf-text-faint">
            // ai-powered cold email outreach
          </div>
        </div>

        <div className="relative max-w-xl">
          <h1 className="font-display text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight cf-text-base">
            Forge cold emails<br />
            that <span style={{ color: "var(--cf-accent)" }}>actually</span><br />
            convert.
          </h1>
          <p className="mt-6 text-sm max-w-md leading-relaxed cf-text-muted">
            Drop a URL or PDF. The AI reads it, writes it, sends it. Your prospects never know it was a template.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 font-mono text-[10px] uppercase tracking-widest">
            <div style={{ borderTop: "1px solid var(--cf-border)", paddingTop: "0.75rem" }}>
              <div className="cf-text-faint">01 //</div>
              <div className="cf-text-base" style={{ marginTop: "0.25rem" }}>CONTEXT</div>
            </div>
            <div style={{ borderTop: "1px solid var(--cf-border)", paddingTop: "0.75rem" }}>
              <div className="cf-text-faint">02 //</div>
              <div className="cf-text-base" style={{ marginTop: "0.25rem" }}>GENERATE</div>
            </div>
            <div style={{ borderTop: "1px solid var(--cf-border)", paddingTop: "0.75rem" }}>
              <div className="cf-text-faint">03 //</div>
              <div className="cf-text-base" style={{ marginTop: "0.25rem" }}>DISPATCH</div>
            </div>
          </div>
        </div>

        <div className="relative font-mono text-[10px] uppercase tracking-widest cf-text-faint">
          &copy; 2026 Coldforge — Built for outbound teams.
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-8 lg:p-16" style={{ background: "var(--cf-bg)" }}>
        <form onSubmit={onSubmit} className="w-full max-w-sm cf-fade">
          <div className="font-mono text-[10px] uppercase tracking-widest mb-2 cf-text-dim">// session_login</div>
          <h2 className="font-display text-3xl font-bold uppercase mb-8 tracking-tight cf-text-base">Sign in</h2>

          <div className="space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest cf-text-dim block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="cf-input"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest cf-text-dim block mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="cf-input"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="cf-btn-primary mt-8 w-full justify-center"
          >
            {busy ? "Authenticating..." : "Sign In →"}
          </button>

          <div
            className="mt-6 font-mono text-[10px] uppercase tracking-widest pt-4 cf-text-faint"
            style={{ borderTop: "1px solid var(--cf-border)" }}
          >
            // access by invite only — contact your workspace admin
          </div>
        </form>
      </div>
    </div>
  );
}
